import { useState, useEffect } from 'react';
import { Send, Mail } from 'lucide-react';

interface EmailSystemSettings {
  id?: number;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: 'none' | 'tls' | 'ssl';
  smtp_auth_required: boolean;
  imap_host?: string;
  imap_port?: number;
  imap_secure?: 'none' | 'tls' | 'ssl';
  sender_name?: string;
  reply_to_email?: string;
  max_attachments_size?: number;
  enabled: boolean;
}

interface EmailSystemSettingsFormProps {
  settings: EmailSystemSettings | null;
  onSave: (settings: EmailSystemSettings) => Promise<void>;
  setSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => void;
}

const EmailSystemSettingsForm: React.FC<EmailSystemSettingsFormProps> = ({
  settings,
  onSave,
  setSaveStatus
}) => {
  const [formData, setFormData] = useState<EmailSystemSettings>({
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: 'tls',
    smtp_auth_required: true,
    imap_host: '',
    imap_port: 993,
    imap_secure: 'ssl',
    sender_name: 'Lernrakete System',
    reply_to_email: '',
    max_attachments_size: 10485760,
    enabled: true
  });

  useEffect(() => {
    if (settings) {
      setFormData({ ...formData, ...settings });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveStatus('saving');
    await onSave(formData);
  };

  const handleInputChange = (field: keyof EmailSystemSettings, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* SMTP Settings */}
      <div>
        <h3 className="text-md font-medium text-gray-900 dark:text-gray-50 mb-4 flex items-center">
          <Send className="mr-2 h-4 w-4" />
          SMTP Einstellungen (Ausgehende E-Mails)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              SMTP Server *
            </label>
            <input
              type="text"
              value={formData.smtp_host}
              onChange={(e) => handleInputChange('smtp_host', e.target.value)}
              className="input-field"
              placeholder="smtp.gmail.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              SMTP Port *
            </label>
            <input
              type="number"
              value={formData.smtp_port}
              onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value))}
              className="input-field"
              min="1"
              max="65535"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              SMTP Verschlüsselung
            </label>
            <select
              value={formData.smtp_secure}
              onChange={(e) => handleInputChange('smtp_secure', e.target.value as 'none' | 'tls' | 'ssl')}
              className="input-field"
            >
              <option value="none">Keine</option>
              <option value="tls">TLS/STARTTLS (Port 587)</option>
              <option value="ssl">SSL (Port 465)</option>
            </select>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="smtp_auth_required"
              checked={formData.smtp_auth_required}
              onChange={(e) => handleInputChange('smtp_auth_required', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="smtp_auth_required" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Authentifizierung erforderlich
            </label>
          </div>
        </div>
      </div>

      {/* IMAP Settings */}
      <div>
        <h3 className="text-md font-medium text-gray-900 dark:text-gray-50 mb-4 flex items-center">
          <Mail className="mr-2 h-4 w-4" />
          IMAP Einstellungen (Eingehende E-Mails)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              IMAP Server (optional)
            </label>
            <input
              type="text"
              value={formData.imap_host || ''}
              onChange={(e) => handleInputChange('imap_host', e.target.value)}
              className="input-field"
              placeholder="imap.gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              IMAP Port
            </label>
            <input
              type="number"
              value={formData.imap_port || 993}
              onChange={(e) => handleInputChange('imap_port', parseInt(e.target.value))}
              className="input-field"
              min="1"
              max="65535"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              IMAP Verschlüsselung
            </label>
            <select
              value={formData.imap_secure || 'ssl'}
              onChange={(e) => handleInputChange('imap_secure', e.target.value as 'none' | 'tls' | 'ssl')}
              className="input-field"
            >
              <option value="none">Keine</option>
              <option value="tls">TLS/STARTTLS (Port 143)</option>
              <option value="ssl">SSL (Port 993)</option>
            </select>
          </div>
        </div>
      </div>

      {/* General Settings */}
      <div>
        <h3 className="text-md font-medium text-gray-900 dark:text-gray-50 mb-4">Allgemeine Einstellungen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Standard Absendername
            </label>
            <input
              type="text"
              value={formData.sender_name || ''}
              onChange={(e) => handleInputChange('sender_name', e.target.value)}
              className="input-field"
              placeholder="Lernrakete System"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Antwort-Adresse (optional)
            </label>
            <input
              type="email"
              value={formData.reply_to_email || ''}
              onChange={(e) => handleInputChange('reply_to_email', e.target.value)}
              className="input-field"
              placeholder="noreply@schule.de"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max. Anhangsgröße (MB)
            </label>
            <input
              type="number"
              value={Math.round((formData.max_attachments_size || 10485760) / 1048576)}
              onChange={(e) => handleInputChange('max_attachments_size', parseInt(e.target.value) * 1048576)}
              className="input-field"
              min="1"
              max="100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Standard: 10 MB</p>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="email_enabled"
              checked={formData.enabled}
              onChange={(e) => handleInputChange('enabled', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="email_enabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              E-Mail System aktiviert
            </label>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Hilfe zur Konfiguration:</h4>
        <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
          <li><strong>Gmail:</strong> SMTP: smtp.gmail.com:587 (TLS), IMAP: imap.gmail.com:993 (SSL)</li>
          <li><strong>Outlook:</strong> SMTP: smtp-mail.outlook.com:587 (TLS), IMAP: outlook.office365.com:993 (SSL)</li>
          <li><strong>Yahoo:</strong> SMTP: smtp.mail.yahoo.com:587 (TLS), IMAP: imap.mail.yahoo.com:993 (SSL)</li>
          <li>Für Gmail benötigen Sie ein App-spezifisches Passwort (nicht Ihr normales Passwort)</li>
        </ul>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
        >
          Einstellungen speichern
        </button>
      </div>
    </form>
  );
};

export default EmailSystemSettingsForm;
